// src/pages/LiveTradesAll.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { FaSyncAlt, FaArrowUp, FaArrowDown, FaListUl } from "react-icons/fa";

/* ---------------- helpers ---------------- */
function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function moneyFmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function sideNorm(side) {
    return String(side || "").toLowerCase();
}

function getPeriodFromRow(row) {
    const p =
        row?.period ??
        row?.period_key ??
        row?.period_name ??
        row?.trade?.period ??
        row?.trade?.period_key ??
        row?.trade?.period_name ??
        "";
    const s = String(p || "").trim();
    return s ? s : null;
}

export default function LiveTradesAll() {
    // Refs for storing the timeout ID and preventing flicker
    const refreshTimeoutRef = useRef(null);
    const initialLoadDoneRef = useRef(false);
    const isMountedRef = useRef(true);

    // Data
    const [tradeList, setTradeList] = useState([]);

    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchLiveTrades = useCallback(async (options = {}) => {
        const { isManualRefresh = false, isAutoRefresh = false } = options;

        // Only set loading states for manual refresh or initial load (not for auto-refresh)
        if (isManualRefresh) {
            setIsRefreshing(true);
        } else if (!isAutoRefresh && !initialLoadDoneRef.current) {
            setIsLoading(true);
        }

        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/live-trades-all`,
                headers: { "Content-Type": "application/json" },
                data: { token: localStorage.getItem("token") || "" },
                timeout: 15000,
            });

            const out = res?.data;

            if (out?.error) {
                if (!isAutoRefresh) {
                    toast.error(typeof out?.error === "string" ? out.error : "Failed to load live trades");
                }
                return;
            }

            const nextList = Array.isArray(out?.data) ? out.data : [];
            setTradeList(nextList);

            if (isManualRefresh) toast.success("Refreshed");

            if (!initialLoadDoneRef.current && !isAutoRefresh) {
                initialLoadDoneRef.current = true;
            }
        } catch (error) {
            if (!isAutoRefresh) {
                if (error?.code === "ECONNABORTED") toast.error("Request timeout");
                else toast.error("Failed to load live trades");
            }
        } finally {
            if (!isAutoRefresh) {
                setIsLoading(false);
                setIsRefreshing(false);
            }
        }
    }, []);

    // Setup auto-refresh every 3 seconds (no loading states)
    const setupAutoRefresh = useCallback(() => {
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);

        refreshTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                fetchLiveTrades({ isAutoRefresh: true });
                setupAutoRefresh();
            }
        }, 3000);
    }, [fetchLiveTrades]);

    // Initial fetch + auto-refresh
    useEffect(() => {
        initialLoadDoneRef.current = false;
        setTradeList([]);

        fetchLiveTrades({});
        setupAutoRefresh();

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [fetchLiveTrades, setupAutoRefresh]);

    // Clear timeout when component unmounts
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, []);

    const onManualRefresh = () => {
        fetchLiveTrades({ isManualRefresh: true });
    };

    // Summary grouped by Coin + Period (only show groups where totalAmount > 0)
    const groupedSummary = useMemo(() => {
        const map = new Map();

        for (const row of tradeList) {
            const e = row?.entity || {};
            const t = row?.trade || {};

            const coinName = String(e?.entity_name || "").trim() || "—";
            const period = getPeriodFromRow(row);

            // Requirement: show totals by coin and period. If period not present, skip group.
            if (!period) continue;

            const key = `${coinName}__${period}`;
            const prev = map.get(key) || {
                coinName,
                period,
                image: e?.image || "",
                totalAmount: 0,
                upAmount: 0,
                downAmount: 0,
                totalCount: 0,
                upCount: 0,
                downCount: 0,
            };

            const amt = Number(t?.amount || 0);
            const side = sideNorm(t?.side);

            prev.totalAmount += amt;
            prev.totalCount += 1;

            if (side === "up") {
                prev.upAmount += amt;
                prev.upCount += 1;
            } else if (side === "down") {
                prev.downAmount += amt;
                prev.downCount += 1;
            }

            // keep latest image if available
            if (!prev.image && e?.image) prev.image = e.image;

            map.set(key, prev);
        }

        const list = Array.from(map.values()).filter((g) => Number(g.totalAmount || 0) !== 0);

        // stable sorting: coinName then period
        list.sort((a, b) => {
            const cn = a.coinName.localeCompare(b.coinName);
            if (cn !== 0) return cn;
            return String(a.period).localeCompare(String(b.period));
        });

        return list;
    }, [tradeList]);

    return (
        <PageTransition>
            <style>{`
                .btn-compact { padding: 4px 8px !important; line-height: 1.1 !important; }
                .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }

                .coin-img { width: 44px; height: 44px; border-radius: 12px; object-fit: cover; }

                .lt-table th, .lt-table td { padding: 6px 6px !important; vertical-align: middle; }
                .lt-table th:not(:last-child), .lt-table td:not(:last-child) { border-right: 1px solid rgba(0,0,0,0.06); }

                .sum-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
                @media (max-width: 991.98px) { .sum-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
                @media (max-width: 575.98px) { .sum-grid { grid-template-columns: repeat(1, minmax(0, 1fr)); } }

                .sum-card {
                    border: 1px solid rgba(0,0,0,0.06);
                    border-radius: 14px;
                    background: #fff;
                    padding: 10px 12px;
                }
                .sum-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
                .sum-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
                .sum-coin { width: 34px; height: 34px; border-radius: 10px; object-fit: cover; }
                .sum-title { font-weight: 800; line-height: 1.1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .sum-sub { font-size: 0.78rem; color: rgba(0,0,0,0.55); }

                .sum-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-top: 10px; }
                .metric {
                    border: 1px solid rgba(0,0,0,0.06);
                    border-radius: 12px;
                    padding: 8px 10px;
                    background: rgba(13,110,253,0.03);
                }
                .metric-label { font-size: 0.72rem; color: rgba(0,0,0,0.55); }
                .metric-value { font-weight: 800; margin-top: 2px; }
                .metric-up { background: rgba(25,135,84,0.06); }
                .metric-down { background: rgba(220,53,69,0.06); }
            `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-3">
                    <div
                        className="coin-img d-flex align-items-center justify-content-center"
                        style={{ background: "rgba(13,110,253,0.12)", color: "#0d6efd" }}
                    >
                        <FaListUl />
                    </div>

                    <div>
                        <h2 className="fw-bold mb-0">Live Trades</h2>
                        <div className="text-muted">All entities • All periods</div>
                    </div>
                </div>

                <div className="d-flex gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-compact d-inline-flex align-items-center gap-2"
                        onClick={onManualRefresh}
                        disabled={isRefreshing || isLoading}
                        title="Refresh"
                    >
                        <FaSyncAlt className={isRefreshing ? "tb-spin" : ""} />
                        {isRefreshing ? "Refreshing..." : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-muted">Loading live trades…</div>
                </div>
            ) : (
                <>
                    {/* Summary by coin + period */}
                    {groupedSummary.length > 0 ? (
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body">
                                <div className="sum-grid">
                                    {groupedSummary.map((g) => (
                                        <div className="sum-card" key={`${g.coinName}__${g.period}`}>
                                            <div className="sum-top">
                                                <div className="sum-left">
                                                    {g.image ? (
                                                        <img
                                                            className="sum-coin"
                                                            src={g.image}
                                                            alt={safeText(g.coinName)}
                                                            onError={(ev) => {
                                                                ev.currentTarget.style.display = "none";
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="sum-coin d-flex align-items-center justify-content-center"
                                                            style={{ background: "rgba(13,110,253,0.10)", color: "#0d6efd" }}
                                                        >
                                                            <FaListUl />
                                                        </div>
                                                    )}

                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="sum-title">{safeText(g.coinName)}</div>
                                                        <div className="sum-sub">
                                                            Period: <span className="mono">{safeText(g.period)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <span className="badge text-bg-light border">
                                                    Trades: <span className="mono">{safeText(g.totalCount)}</span>
                                                </span>
                                            </div>

                                            <div className="sum-metrics">
                                                <div className="metric">
                                                    <div className="metric-label">Total</div>
                                                    <div className="metric-value">{moneyFmt(g.totalAmount, 2)}</div>
                                                </div>

                                                <div className="metric metric-up">
                                                    <div className="metric-label">Up</div>
                                                    <div className="metric-value">
                                                        {moneyFmt(g.upAmount, 2)}
                                                        <span className="text-muted ms-2 mono" style={{ fontWeight: 600 }}>
                                                            ({safeText(g.upCount)})
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="metric metric-down">
                                                    <div className="metric-label">Down</div>
                                                    <div className="metric-value">
                                                        {moneyFmt(g.downAmount, 2)}
                                                        <span className="text-muted ms-2 mono" style={{ fontWeight: 600 }}>
                                                            ({safeText(g.downCount)})
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Table (same design, trade_id removed) */}
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="p-2 table-responsive">
                                <table className="table table-sm align-middle mb-0 lt-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 90 }}>Side</th>
                                            <th style={{ width: 110 }} className="text-end">
                                                Amount
                                            </th>

                                            <th style={{ width: 90 }}>Coin</th>
                                            <th style={{ width: 220 }}>Entity</th>
                                            <th style={{ width: 220 }}>Period</th>

                                            <th style={{ width: 140 }}>User</th>
                                            <th style={{ width: 220 }}>Name</th>
                                            <th style={{ width: 220 }}>Mobile</th>
                                            <th style={{ width: 260 }}>Email</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {tradeList.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="text-center text-muted py-5">
                                                    <div className="fw-semibold">No live trades</div>
                                                </td>
                                            </tr>
                                        ) : (
                                            tradeList.map((row, idx) => {
                                                const u = row?.user || {};
                                                const t = row?.trade || {};
                                                const e = row?.entity || {};
                                                const s = sideNorm(t?.side);

                                                const mobileDisplay =
                                                    safeText(u?.mobile) === "—"
                                                        ? "—"
                                                        : `+${safeText(u?.country_code) === "—" ? "" : safeText(u?.country_code)} ${safeText(
                                                            u?.mobile
                                                        )}`.trim();

                                                return (
                                                    <tr key={`${safeText(t?.trade_id) || "row"}-${idx}`}>
                                                        <td>
                                                            {s === "up" ? (
                                                                <span className="badge text-bg-success d-inline-flex align-items-center gap-1">
                                                                    <FaArrowUp /> Up
                                                                </span>
                                                            ) : s === "down" ? (
                                                                <span className="badge text-bg-danger d-inline-flex align-items-center gap-1">
                                                                    <FaArrowDown /> Down
                                                                </span>
                                                            ) : (
                                                                <span className="badge text-bg-secondary">—</span>
                                                            )}
                                                        </td>

                                                        <td className="text-end fw-semibold">{moneyFmt(t?.amount, 2)}</td>

                                                        <td>
                                                            {e?.image ? (
                                                                <img
                                                                    src={e.image}
                                                                    alt={safeText(e?.entity_name)}
                                                                    width="22"
                                                                    height="22"
                                                                    style={{ borderRadius: 8, objectFit: "cover" }}
                                                                    onError={(ev) => {
                                                                        ev.currentTarget.style.display = "none";
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>

                                                        <td className="fw-semibold">{safeText(e?.entity_name)}</td>

                                                        <td className="mono fw-semibold">{safeText(t?.period)}</td>
                                                        <td className="mono fw-semibold">{safeText(u?.user_id)}</td>
                                                        <td className="fw-semibold">{safeText(u?.name)}</td>
                                                        <td className="fw-semibold">{mobileDisplay}</td>
                                                        <td className="fw-semibold" style={{ wordBreak: "break-word" }}>
                                                            {safeText(u?.email)}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </PageTransition>
    );
}
